import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axiosInstance';

const CompanyQuestions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [form, setForm] = useState({
    Question: '',
    Company: '',
    TechStack: '',
    DiffType: 'Medium',
    Answer: ''
  });

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [companies] = useState(['Google', 'Amazon', 'Microsoft', 'Infosys', 'TCS']);
  const [techStacks] = useState(['ReactJs', 'JavaScript', 'Python', 'Java', 'NodeJs', 'Angular', 'VueJs', 'TypeScript']);

  useEffect(() => {
    if (isEditing) {
      fetchQuestion();
    }
  }, [id]);

  const fetchQuestion = async () => {
    try {
      const response = await api.get(`/api/companyquestions/${id}`);
      const question = response.data.data;
      setForm(question);
    } catch (error) {
      console.error('Error fetching company question:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.Question || !form.Company || !form.TechStack || !form.Answer) {
      alert('Question, Company, Tech Stack, and Answer are required');
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        await api.put(`/api/companyquestions/${id}`, form);
      } else {
        await api.post('/api/companyquestions', form);
      }
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Error saving company question:', error);
      alert('Error saving company question. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if it's a CSV file
    if (!file.name.endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('companyQuestionFile', file);

    setUploading(true);
    try {
      const response = await api.post('/api/companyquestions/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      alert(`Successfully uploaded ${response.data.importedCount || 0} company questions`);
      // Reset file input
      e.target.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.response?.data?.message || 'Error uploading file');
    } finally {
      setUploading(false);
    }
  };

  
  
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">
          {isEditing ? 'Edit Company Question' : 'Add Company Question'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Question */}
        <div>
          <label className="block text-sm font-medium mb-2">Question *</label>
          <textarea
            value={form.Question}
            onChange={(e) => setForm({ ...form, Question: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            rows={4}
            placeholder="Enter company question..."
            required
          />
        </div>

        {/* Company */}
        <div>
          <label className="block text-sm font-medium mb-2">Company *</label>
          <select
            value={form.Company}
            onChange={(e) => setForm({ ...form, Company: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          >
            <option value="">Select Company</option>
            {companies.map(company => (
              <option key={company} value={company}>{company}</option>
            ))}
          </select>
        </div>

        {/* Tech Stack */}
        <div>
          <label className="block text-sm font-medium mb-2">Tech Stack *</label>
          <select
            value={form.TechStack}
            onChange={(e) => setForm({ ...form, TechStack: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          >
            <option value="">Select Tech Stack</option>
            {techStacks.map(tech => (
              <option key={tech} value={tech}>{tech}</option>
            ))}
          </select>
        </div>

        {/* Difficulty Type */}
        <div>
          <label className="block text-sm font-medium mb-2">Difficulty Type *</label>
          <select
            value={form.DiffType}
            onChange={(e) => setForm({ ...form, DiffType: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          >
            <option value="Easy">Easy</option>
            <option value="Medium">Medium</option>
            <option value="Hard">Hard</option>
          </select>
        </div>

        {/* Answer */}
        <div>
          <label className="block text-sm font-medium mb-2">Answer *</label>
          <textarea
            value={form.Answer}
            onChange={(e) => setForm({ ...form, Answer: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            rows={3}
            placeholder="Enter the correct answer..."
            required
          />
        </div>

        {/* Submit and Upload Buttons */}
        <div className="flex justify-between">
          <div className="flex gap-3">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
              id="csv-upload-input"
              disabled={uploading}
            />
            <button
              type="button"
              onClick={() => document.getElementById('csv-upload-input').click()}
              disabled={uploading}
              className="bg-secondary text-secondary-foreground px-4 py-3 rounded-md hover:bg-secondary/80 disabled:opacity-50 transition-colors"
            >
              {uploading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-secondary-foreground border-t-transparent"></div>
                  Uploading...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Icon name="Upload" size={16} />
                  Upload CSV
                </div>
              )}
            </button>
          </div>
          
          <button
            type="submit"
            disabled={loading || uploading}
            className="bg-primary text-primary-foreground px-6 py-3 rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground border-t-transparent"></div>
                {isEditing ? 'Updating...' : 'Creating...'}
              </div>
            ) : (
              <span>{isEditing ? 'Update Company Question' : 'Create Company Question'}</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyQuestions;
